import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type CursorMode = 'default' | 'link' | 'view'

const INTERACTIVE = 'a, button, [role="button"], input, textarea, select, label, summary'

/**
 * A soft follower ring for mouse users. Hovering anything marked `data-cursor="view"`
 * turns it into a "View" bubble. Never rendered on touch screens or with reduced motion.
 */
export const Cursor: FC = (): ReactNode => {
    const ringRef = useRef<HTMLDivElement>(null)
    const [enabled, setEnabled] = useState(false)
    const [visible, setVisible] = useState(false)
    const [mode, setMode] = useState<CursorMode>('default')

    useEffect(() => {
        const query = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)')
        const update = (): void => setEnabled(query.matches)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        const ring = ringRef.current

        if (!enabled || !ring) return

        let targetX = -100
        let targetY = -100
        let x = -100
        let y = -100
        let frame = 0
        let running = false
        let current: CursorMode = 'default'

        const tick = (): void => {
            x += (targetX - x) * 0.2
            y += (targetY - y) * 0.2
            ring.style.transform = `translate3d(${x}px, ${y}px, 0)`

            if (Math.abs(targetX - x) < 0.1 && Math.abs(targetY - y) < 0.1) {
                running = false
                return
            }

            frame = window.requestAnimationFrame(tick)
        }

        const start = (): void => {
            if (running) return
            running = true
            frame = window.requestAnimationFrame(tick)
        }

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse') return

            if (x === -100 && y === -100) {
                x = event.clientX
                y = event.clientY
            }

            targetX = event.clientX
            targetY = event.clientY
            setVisible(true)
            start()

            const element = event.target instanceof Element ? event.target : null
            const next: CursorMode = element?.closest('[data-cursor="view"]')
                ? 'view'
                : element?.closest(INTERACTIVE) ? 'link' : 'default'

            if (next !== current) {
                current = next
                setMode(next)
            }
        }

        const onLeave = (): void => setVisible(false)

        window.addEventListener('pointermove', onMove, { passive: true })
        document.documentElement.addEventListener('pointerleave', onLeave)

        return () => {
            window.cancelAnimationFrame(frame)
            window.removeEventListener('pointermove', onMove)
            document.documentElement.removeEventListener('pointerleave', onLeave)
        }
    }, [enabled])

    if (!enabled) return null

    return (
        <div
            ref={ringRef}
            aria-hidden='true'
            className={cn(
                'pointer-events-none fixed left-0 top-0 z-[100] transition-opacity duration-300',
                visible ? 'opacity-100' : 'opacity-0',
                mode !== 'view' && 'mix-blend-difference'
            )}
            style={{ transform: 'translate3d(-100px, -100px, 0)' }}
        >
            <div
                className={cn(
                    'flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-[width,height,background-color,border-color] duration-300 ease-out',
                    mode === 'default' && 'size-8 border-[1.5px] border-white',
                    mode === 'link' && 'size-14 border-[1.5px] border-white bg-white/10',
                    mode === 'view' && 'size-24 border-0 bg-brand text-brand-foreground shadow-[0_12px_40px_-12px_rgba(43,53,255,0.7)]'
                )}
            >
                <span
                    className={cn(
                        'font-mono text-[11px] font-medium uppercase tracking-[0.16em] transition-opacity duration-200',
                        mode === 'view' ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    View
                </span>
            </div>
        </div>
    )
}
